import { useEffect, useMemo, useState } from "react";
import { groupBy } from "@repo/utils";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useHover } from "react-aria";

import type { NavigationItem } from "~/managers/use-navigation-manager";
import { cn } from "~/lib/utils";
import AudioButton from "../ui/audio-button";

export function Sidebar({ navItems }: { navItems: NavigationItem[] }) {
  const router = useRouterState();
  const navigate = useNavigate();

  const groupedNavItems = useMemo(
    () => groupBy(navItems, "section"),
    [navItems],
  );

  const { hoverProps } = useHover({
    onHoverStart() {
      setNavExpended(true);
    },
    onHoverEnd() {
      setNavExpended(false);
    },
  });

  const handleTouchStart = (event: React.MouseEvent<HTMLDivElement>): void => {
    if ((event.target as HTMLElement).classList.contains("sidebar")) {
      setNavExpended(true);
    }
  };

  const handleDocumentClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    if (!target.closest(".sidebar") && !target.closest(".overlaying-element")) {
      setNavExpended(false);
    }
  };

  const [navExpended, setNavExpended] = useState(false);

  useEffect(() => {
    document.addEventListener("click", handleDocumentClick);

    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, []);

  return (
    <div className="hidden z-[1000] relative h-full w-16 flex-col md:flex">
      <nav
        {...hoverProps}
        onClick={handleTouchStart}
        data-state={navExpended ? "expanded" : "collapsed"}
        className="sidebar relative border-default overflow-hidden transition-width bg-background-secondary hide-scrollbar group z-10 flex h-full w-16 flex-col justify-between overflow-y-auto border-r py-2 duration-200 data-[state=expanded]:w-[13rem] data-[state=expanded]:shadow-xl"
      >
        {Object.entries(groupedNavItems).map(([section, items]) => (
          <ul
            key={section}
            onClick={(e) => e.stopPropagation()}
            className="flex flex-col justify-start gap-y-2 px-2"
          >
            {items
              .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
              .map((item) => (
                <AudioButton
                  onClick={() => {
                    setNavExpended(false);
                    void navigate({ to: item.href });
                  }}
                  aria-current={
                    router.location.pathname === item.href ? true : false
                  }
                  className={cn(
                    router.location.pathname === item.href &&
                      "bg-gray-200 dark:bg-bunker-900",
                    "group/item relative text-left flex h-12 w-12 items-center rounded text-foreground transition-all duration-200 dark:hover:bg-bunker-900 hover:bg-gray-200 hover:text-foreground group-data-[state=expanded]:w-full group-data-[state=collapsed]:justify-center group-data-[state=expanded]:-space-x-2",
                  )}
                >
                  {item.icon && (
                    <div className="absolute left-0 top-0 flex h-12 w-12 text-[2rem] items-center justify-center rounded">
                      {router.location.pathname === item.href &&
                      item.currentIcon ? (
                        <item.currentIcon fontSize="inherit" />
                      ) : (
                        <item.icon fontSize="inherit" />
                      )}
                    </div>
                  )}

                  <span className="absolute left-7 min-w-[128px] text-md text-foreground opacity-0 transition-all group-hover/item:text-foreground group-aria-current/item:text-foreground group-data-[state=expanded]:left-16 group-data-[state=expanded]:opacity-100">
                    {item.name}
                  </span>
                </AudioButton>
              ))}
          </ul>
        ))}
      </nav>
    </div>
  );
}
